import { useState } from 'react'
import { Box, Button, FormHelperText, Paper, Stack, TextField, Typography } from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import AddressAutocomplete from 'components/AddressAutocomplete'
import { EditIcon } from 'components/Icons'
import formatAddress, { placeToAddress } from 'utils/formatAddress'
import useSaveSection from '../useSaveSection'

const REQUIRED_FIELDS = ['street', 'city', 'state', 'zip', 'country']
const COMPARED_FIELDS = ['street', 'line2', 'city', 'state', 'zip', 'country']

const Labeled = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {children}
  </Box>
)

export default function AddressSection({ request, onClose }) {
  const { t } = useTranslation()
  const { save, saving } = useSaveSection(request)
  const [initial] = useState(() => ({ country: 'US', ...request.address, line2: request.address?.line2 ?? '' }))
  const [address, setAddress] = useState(initial)
  const [editing, setEditing] = useState(false)
  const [incomplete, setIncomplete] = useState(false)

  const previous = request.primaryParentAddresses ?? []

  const onPlaceSelected = place => {
    const found = placeToAddress(place)
    setAddress(prev => ({ ...prev, ...found }))
    setIncomplete(Object.values(found).some(value => !value))
  }

  const stopEditing = () => {
    setAddress(initial)
    setIncomplete(false)
    setEditing(false)
  }

  const complete = REQUIRED_FIELDS.every(key => String(address[key] ?? '').trim())
  const changed = COMPARED_FIELDS.some(key => String(address[key] ?? '') !== String(initial[key] ?? ''))

  const onSave = async () => {
    const { street, line2, city, state, zip, country } = address
    const payload = { type: 'home', street, line2: line2 || null, city, state, zip, country }
    if (await save({ address: payload }, 'Address updated')) onClose()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={t('New Address')} small close={onClose} borderBottom />

      <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        <Labeled label={t('Previous Address')}>
          {previous.length ? (
            previous.map((item, index) => (
              <Typography key={index} variant="body2">
                {formatAddress(item)}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('No previous address on file')}
            </Typography>
          )}
        </Labeled>

        <Labeled label={t('Requested address')}>
          <Typography variant="body2" fontWeight={600}>
            {formatAddress(initial) || '--'}
          </Typography>
        </Labeled>

        {!editing ? (
          <Box>
            <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              {t('Correct the address')}
            </Button>
            <FormHelperText>{t('Only needed if the requested address is wrong.')}</FormHelperText>
          </Box>
        ) : (
          <>
            <Box>
              <AddressAutocomplete label={t('Search for the corrected address')} onPlaceSelected={onPlaceSelected} />
              {incomplete && (
                <FormHelperText error>
                  {t('The selected address is incomplete, search for a more specific one')}
                </FormHelperText>
              )}
            </Box>

            <TextField
              fullWidth
              size="small"
              label={t('Apartment, suite, etc.')}
              value={address.line2}
              onChange={event => setAddress(prev => ({ ...prev, line2: event.target.value }))}
            />

            {changed && complete && (
              <Labeled label={t('Corrected address')}>
                <Paper variant="outlined" sx={{ px: 1.5, py: 1, mt: 0.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <LocationOnIcon color="action" />
                    <Typography variant="body2">
                      {[formatAddress(address), address.country].filter(Boolean).join(', ')}
                    </Typography>
                  </Stack>
                </Paper>
              </Labeled>
            )}

            <Box>
              <Button size="small" onClick={stopEditing}>
                {t('Keep requested address')}
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button variant="contained" disabled={saving || !editing || !changed || !complete} onClick={onSave}>
          {t('Save')}
        </Button>
      </Stack>
    </Box>
  )
}
